import type { History, Location } from "history";
import type { ShareEntry } from "./ShareFlowProvider";

export interface ShareParams {
  share: ShareEntry;
  design?: number;
  reviews?: number[];
  theme?: number;
  /** backgroundAssetId */
  bg?: number;
  /** The player's uploaded photo, only until `render` is written. */
  bgUrl?: string;
  render?: number;
  mode?: "carousel";
}

const SHARE_KEYS = ["share", "design", "reviews", "theme", "bg", "bgUrl", "render", "mode"] as const;

function parseId(value: string | null): number | undefined {
  if (value == null || value === "") return undefined;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

function parseEntry(value: string | null): ShareEntry | null {
  if (!value) return null;
  const [kind, ...ids] = value.split(":");
  if (kind === "review") {
    const reviewId = parseId(ids[0] ?? null);
    return reviewId ? { kind: "review", reviewId } : null;
  }
  if (kind === "prompt") {
    const promptId = parseId(ids[0] ?? null);
    const designPackageId = parseId(ids[1] ?? null);
    return promptId && designPackageId ? { kind: "library", promptId, designPackageId } : null;
  }
  return null;
}

function serializeEntry(entry: ShareEntry): string | null {
  if (entry.kind === "review") return `review:${entry.reviewId}`;
  if (entry.kind === "library") return `prompt:${entry.promptId}:${entry.designPackageId}`;
  return null;
}

export function parseShareParams(search: string): ShareParams | null {
  const params = new URLSearchParams(search);
  const share = parseEntry(params.get("share"));
  if (!share) return null;

  const result: ShareParams = { share };
  const design = parseId(params.get("design"));
  if (design) result.design = design;
  const reviews = (params.get("reviews") ?? "")
    .split(",")
    .map(id => parseId(id))
    .filter((id): id is number => id != null);
  if (reviews.length > 0) result.reviews = reviews;
  const theme = parseId(params.get("theme"));
  if (theme) result.theme = theme;
  const bg = parseId(params.get("bg"));
  if (bg) result.bg = bg;
  const bgUrl = params.get("bgUrl");
  if (bgUrl) result.bgUrl = bgUrl;
  const render = parseId(params.get("render"));
  if (render) result.render = render;
  if (params.get("mode") === "carousel") result.mode = "carousel";
  return result;
}

function serializeValue(key: (typeof SHARE_KEYS)[number], value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (key === "share") return serializeEntry(value as ShareEntry);
  if (key === "reviews") {
    const ids = value as number[];
    return ids.length > 0 ? ids.join(",") : null;
  }
  return String(value);
}

// The coach prompt page keeps `tab` with a hook that writes window.location directly, so the
// router's location may not have it yet.
function carryTab(params: URLSearchParams, location: Location) {
  if (typeof window === "undefined" || params.has("tab")) return;
  if (window.location.pathname !== location.pathname) return;
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (tab) params.set("tab", tab);
}

function replaceSearch(history: History, location: Location, edit: (params: URLSearchParams) => void) {
  // history.location is current even when two writers run in the same tick.
  const current = history.location ?? location;
  const params = new URLSearchParams(current.search);
  edit(params);
  carryTab(params, current);
  const search = params.toString();
  const next = search ? `?${search}` : "";
  if (next === current.search || (next === "" && !current.search)) return;
  // Keep the router state (such as where the editor's back link goes).
  history.replace({ pathname: current.pathname, search: next, state: current.state });
}

/**
 * Merges the given share keys into the search (a key set to undefined is removed) and leaves every
 * other param alone. `null` removes all share keys.
 */
export function writeShareParams(history: History, location: Location, patch: Partial<ShareParams> | null): void {
  replaceSearch(history, location, params => {
    if (patch === null) {
      SHARE_KEYS.forEach(key => params.delete(key));
      return;
    }
    SHARE_KEYS.forEach(key => {
      if (!(key in patch)) return;
      const value = serializeValue(key, patch[key]);
      if (value === null) params.delete(key);
      else params.set(key, value);
    });
  });
}

export function readSelectedReviewParam(search: string): number | null {
  return parseId(new URLSearchParams(search).get("review")) ?? null;
}

export function writeSelectedReviewParam(history: History, location: Location, reviewId: number | null): void {
  replaceSearch(history, location, params => {
    if (reviewId == null) params.delete("review");
    else params.set("review", String(reviewId));
  });
}
